import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Input, Modal, Select } from '@components/common';
import SelectSpinner from '@components/common/SelectSpinner';
import Logo from '@components/ModalComponents/OrganisationForm/BasicInfo/Logo';
import UploadLogo from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo';
import { TCreateUploadUrl } from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo/UploadLogo';

import { useMutateCreateBrandingSetup } from '@hooks/organisation/useMutateCreateBrandingSetup';
import { useMutateEditBrandingSetup } from '@hooks/organisation/useMutateEditBrandingSetup';
import { useQueryAllOrganisations } from '@hooks/organisation/useQueryAllOrganisations';
import { useUploadLogo } from '@hooks/useUploadLogo';

import { URL } from '@api/urls';

import { isEmpty } from '@utils/common';
import { convertBytesToSize } from '@utils/fileSizeFormatter';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IBrandingSetup, ICreateSetupFormValues } from '@interfaces/platform/branding';

import { EValidationMode } from '@enums/validationMode';

import { defaultBrandingSetupName } from '@constants/common';
import { MODAL_WIDTH_580 } from '@constants/modals';
import { fileErrors } from './constants';

import { schema } from './schema';

import styles from './BrandingSetupForm.module.scss';

const {
	createUploadBrandingLogoUrl,
} = URL;

const MAX_BYTES_FOR_LOGO = 1.05e+6;

interface ICreateSetupFormProps {
	closeModals: () => void;
	onClose: () => void;
	isEditMode: boolean;
	editableSetup?: IBrandingSetup | null;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
}

const BrandingSetupForm = ({
	closeModals,
	onClose,
	isEditMode,
	editableSetup,
	setIsDirty,
}: ICreateSetupFormProps) => {
	const [selectedSpinnerId, setSelectedSpinnerId] = useState(editableSetup?.spinnerId || 0);
	const [selectedSpinnerColorId, setSelectedSpinnerColorId] = useState(editableSetup?.spinnerColorId || 0);

	const [isOpenLogoModal, setIsOpenLogoModal] = useState(false);

	const toggleLogoModal = () => setIsOpenLogoModal(prevState => !prevState);

	const {
		register,
		watch,
		setValue,
		formState: { errors, isDirty },
		handleSubmit,
		clearErrors,
		control,
	} = useForm<ICreateSetupFormValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			setupName: editableSetup?.name,
			logoCaption: editableSetup?.logoCaption,
			logoId: editableSetup?.logo.id,
			organisations: editableSetup?.organizations?.map(organisation => {
				return {
					id: organisation.id,
					value: organisation.id,
					label: organisation.name,
					disabled: isEditMode,
				};
			}),
		},
	});

	const setLogoId = (id: string) => setValue('logoId', id);

	const removeLogo = () => setLogoId('');

	const logoId = watch('logoId');
	const organisations = watch('organisations');

	const initialSelectedOrgsLength = useRef(organisations?.length || 0);
	const [isSelectedOrganisationsChanged, setIsSelectedOrganisationsChanged] = useState(false);

	useEffect(() => {
		setIsSelectedOrganisationsChanged(initialSelectedOrgsLength.current !== organisations?.length);
	}, [organisations]);

	const {
		createUploadLogoUrl,
		progress,
		isLoadingCreateUploadLogoUrl,
	} = useUploadLogo({ setLogoId, toggleLogoModal, url: createUploadBrandingLogoUrl });

	const { handleCreateSetup, isLoadingCreateSetup } = useMutateCreateBrandingSetup([closeModals]);
	const { handleEditBrandingSetup, isLoadingEditBrandingSetup } = useMutateEditBrandingSetup([closeModals]);

	const { organisationsAll } = useQueryAllOrganisations();

	const organisationsOptions = organisationsAll.filter(organisation => {
		return !organisations?.find((item) => item?.id === organisation.id)?.disabled;
	}).map(organisation => {
		return {
			id: organisation.id,
			value: organisation.id,
			label: organisation.name,
		};
	});

	useEffect(() => {
		if (!!errors.logoId && logoId) clearErrors('logoId');
	}, [logoId, clearErrors, errors.logoId]);

	const onChangeOrganisations = (options: TSelectOption<string>[]) => setValue('organisations', options);

	const onSave = (values: ICreateSetupFormValues) => {
		const dto = {
			logoCaption: values?.logoCaption,
			logoId: values.logoId || null,
			organizationsIds: values?.organisations?.map(organisation => organisation?.id as string) || [],
			spinnerId: selectedSpinnerId,
			spinnerColorId: selectedSpinnerColorId,
			name: values.setupName,
		};

		if (isEditMode && editableSetup) {
			handleEditBrandingSetup({
				setupId: editableSetup.id,
				dto,
			});

			return;
		}

		handleCreateSetup(dto);
	};

	const isSetupNameDisabled = isEditMode && editableSetup?.name === defaultBrandingSetupName;

	const multiselectText = organisations?.length && organisations.length > 1
		? 'organisations selected'
		: 'organisation selected';

	const maxSizeInfoText = `File size: up to ${convertBytesToSize(MAX_BYTES_FOR_LOGO)}`;

	useEffect(() => setIsDirty(isDirty), [isDirty, setIsDirty]);

	return (
		<>
			{isOpenLogoModal && (
				<Modal
					isOpen={isOpenLogoModal}
					isScrollableContent
					onClose={toggleLogoModal}
					title='Upload image'
					width={MODAL_WIDTH_580}
				>
					<UploadLogo
						circularCrop={false}
						createUploadUrl={createUploadLogoUrl as unknown as TCreateUploadUrl}
						isLoadingCreateUploadUrl={isLoadingCreateUploadLogoUrl}
						logoFileErrors={fileErrors}
						maxSize={MAX_BYTES_FOR_LOGO}
						maxSizeInfoText={maxSizeInfoText}
						progress={progress}
						toggleLogoModal={toggleLogoModal}
					/>
				</Modal>
			)}
			<div className={styles.basicInfoContainer}>
				<div className={styles.inputsContainer}>
					<Input
						errorMessage={errors?.setupName?.message}
						isError={!!errors.setupName}
						isReadOnly={isSetupNameDisabled}
						label='Setup Name'
						name='setupName'
						placeholder='Enter setup name'
						register={register}
						required
					/>
					<div className={styles.logoCaption}>
						<Input
							errorMessage={errors?.logoCaption?.message}
							isError={!!errors.logoCaption}
							label='Logo Caption'
							name='logoCaption'
							placeholder='Enter logo caption'
							register={register}
						/>
					</div>
				</div>
				<div>
					<Logo
						avatarId={logoId}
						description='Upload logo icon'
						isLogoSquare
						isRequired
						openLogoModal={toggleLogoModal}
						removeLogo={removeLogo}
					/>
					{!!errors?.logoId
						&& <div className={styles.logoError}>
							{errors?.logoId?.message}
						</div>
					}
				</div>
			</div>
			<SelectSpinner
				selectedSpinnerColorId={selectedSpinnerColorId}
				selectedSpinnerId={selectedSpinnerId}
				setSelectedSpinnerColorId={setSelectedSpinnerColorId}
				setSelectedSpinnerId={setSelectedSpinnerId}
			/>
			<div className={styles.selectWrapper}>
				<Select
					closeMenuOnSelect={false}
					control={control}
					handleChange={onChangeOrganisations}
					isClearable={!isEmpty(organisationsOptions) && isSelectedOrganisationsChanged}
					isError={!!errors.organisations}
					isMulti
					label='Organisations'
					multiselectText={multiselectText}
					name='organisations'
					options={organisationsOptions}
					placeholder='Choose organisations'
					value={organisations}
				/>
			</div>
			<div className={styles.submitButtons}>
				<Button
					onClick={onClose}
					text='Cancel'
					withoutBackground
				/>
				<Button
					className={styles.saveButton}
					disabled={!isEmpty(errors)}
					isLoading={isLoadingCreateSetup || isLoadingEditBrandingSetup}
					large
					onClick={handleSubmit(onSave)}
					text='Save'
				/>
			</div>
		</>
	);
};

export default BrandingSetupForm;
